/**
 * Serviço de transações normalizadas — visão unificada multi-broker.
 * Agrega movimentações de XP, BTG, Inter/Interwin e Avenue em formato único.
 */
import { supabase } from "./core.js";
import { logError, logWarn } from "../shared/logger.js";

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

interface TransactionFilters {
  broker?: string;
  type?: string;
  ticker?: string;
  assetClass?: string;
  dateFrom?: string;
  dateTo?: string;
  period?: string;
  limit?: number;
}

interface TransactionRow {
  id?: string;
  client_id: string;
  broker: string;
  type: string;
  ticker: string | null;
  asset_name: string;
  asset_class: string;
  currency: string;
  quantity: number | null;
  unit_price: number | null;
  gross_value: number;
  net_value: number | null;
  tx_date: string;
  ref_period: string | null;
  snapshot_id: string | null;
  source: string;
  raw_data: unknown;
}

interface NormalizedTx {
  client_id: string;
  broker: string;
  type: string;
  ticker: string | null;
  asset_name: string;
  asset_class: string;
  currency: string;
  quantity: number | null;
  unit_price: number | null;
  gross_value: number;
  net_value: number | null;
  tx_date: string;
  ref_period: string | null;
  snapshot_id: string | null;
  source: string;
  raw_data: unknown;
}

interface TypeSummary {
  count: number;
  totalGross: number;
  totalNet: number;
}

interface InterwinAsset {
  nome?: string;
  ticker?: string;
  estrategia?: string;
  aplicacoes?: number;
  resgates?: number;
  eventos?: number;
  [key: string]: unknown;
}

interface InterwinMovimentacao {
  tipo?: string;
  data?: string;
  valor?: number;
  [key: string]: unknown;
}

interface InterwinParsed {
  period?: string | null;
  ref_date?: string | null;
  movimentacoes?: InterwinMovimentacao[];
  assets_inter?: InterwinAsset[];
  [key: string]: unknown;
}

interface XPAsset {
  n?: string;
  v?: number;
  liq?: number;
  [key: string]: unknown;
}

interface XPAssets {
  posfix?: XPAsset[];
  inflacao?: XPAsset[];
  prefixado?: XPAsset[];
  prev?: XPAsset[];
}

interface XPParsed {
  period?: string | null;
  assets_xp?: XPAssets;
  [key: string]: unknown;
}

interface BTGAsset {
  code?: string;
  bruto?: number;
  liq?: number;
  [key: string]: unknown;
}

interface BTGParsed {
  assets_btg?: BTGAsset[];
  [key: string]: unknown;
}

interface AvenuePosition {
  ticker?: string;
  name?: string;
  shares?: number | null;
  price?: number | null;
  value?: number;
  [key: string]: unknown;
}

interface AvenueParsed {
  period?: string | null;
  positions?: AvenuePosition[];
  source?: string;
  [key: string]: unknown;
}

interface IngestResult {
  count: number;
  transactions: NormalizedTx[];
}

// ═══════════════════════════════════════════════════════
// CRUD — Supabase
// ═══════════════════════════════════════════════════════

export async function fetchTransactions(
  clientId: string,
  filters: TransactionFilters = {}
): Promise<TransactionRow[]> {
  let q = supabase
    .from("transactions")
    .select(
      "id, client_id, broker, type, ticker, asset_name, asset_class, currency, quantity, unit_price, gross_value, net_value, tx_date, ref_period, snapshot_id, source, raw_data"
    )
    .eq("client_id", clientId)
    .order("tx_date", { ascending: false });

  if (filters.broker) q = q.eq("broker", filters.broker);
  if (filters.type) q = q.eq("type", filters.type);
  if (filters.ticker) q = q.eq("ticker", filters.ticker);
  if (filters.assetClass) q = q.eq("asset_class", filters.assetClass);
  if (filters.dateFrom) q = q.gte("tx_date", filters.dateFrom);
  if (filters.dateTo) q = q.lte("tx_date", filters.dateTo);
  if (filters.period) q = q.eq("ref_period", filters.period);
  q = q.limit(filters.limit || 500);

  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function insertTransactions(rows: NormalizedTx[]): Promise<void> {
  if (!rows?.length) return;
  const { error } = await supabase.from("transactions").insert(rows);
  if (error) throw error;
}

export async function deleteTransactionsBySnapshot(
  clientId: string,
  snapshotId: string
): Promise<void> {
  const { error } = await supabase
    .from("transactions")
    .delete()
    .eq("client_id", clientId)
    .eq("snapshot_id", snapshotId);
  if (error) throw error;
}

export async function deleteTransactionsByPeriod(
  clientId: string,
  broker: string,
  refPeriod: string
): Promise<void> {
  const { error } = await supabase
    .from("transactions")
    .delete()
    .eq("client_id", clientId)
    .eq("broker", broker)
    .eq("ref_period", refPeriod);
  if (error) throw error;
}

export async function fetchTransactionSummary(
  clientId: string,
  filters: TransactionFilters = {}
): Promise<Record<string, TypeSummary>> {
  const txs = await fetchTransactions(clientId, { ...filters, limit: 5000 });
  const summary: Record<string, TypeSummary> = {};
  for (const tx of txs) {
    if (!summary[tx.type]) {
      summary[tx.type] = { count: 0, totalGross: 0, totalNet: 0 };
    }
    summary[tx.type].count++;
    summary[tx.type].totalGross += tx.gross_value || 0;
    summary[tx.type].totalNet += tx.net_value || tx.gross_value || 0;
  }
  return summary;
}

// ═══════════════════════════════════════════════════════
// NORMALIZAÇÃO — Converte saída dos parsers em transações
// ═══════════════════════════════════════════════════════

const VALID_TYPES = new Set([
  "compra",
  "venda",
  "dividendo",
  "jcp",
  "juros",
  "amortizacao",
  "resgate",
  "aplicacao",
  "evento",
  "outro",
]);

const VALID_CLASSES = new Set([
  "renda_fixa",
  "renda_variavel",
  "fundos",
  "previdencia",
  "internacional",
  "cripto",
  "outros",
]);

const normalizeType = (raw: string | null | undefined): string => {
  if (!raw) return "outro";
  const s = String(raw)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

  if (/compra|buy|purchase|aplicacao|aplicação/.test(s)) return "aplicacao";
  if (/venda|sell|resgate/.test(s)) return "resgate";
  if (/dividend|provento|rendimento/.test(s)) return "dividendo";
  if (/jcp|juros\s+sobre\s+capital/.test(s)) return "jcp";
  if (/juros|cupom|coupon|rendimento|yield/.test(s)) return "juros";
  if (/amortiza/.test(s)) return "amortizacao";
  if (/evento|bonificacao|desdobramento|split|grupamento/.test(s)) return "evento";

  return "outro";
};

const inferAssetClass = (
  name: string | null | undefined,
  tipo: string | null | undefined,
  ticker: string | null | undefined,
  broker: string
): string => {
  const s = [name, tipo, ticker]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (broker === "avenue") return "internacional";
  if (/prev|vgbl|pgbl/.test(s)) return "previdencia";
  if (/fundo|fi-|fic|fii|fi |fidc/.test(s)) return "fundos";
  if (/cripto|bitcoin|btc|eth|crypto/.test(s)) return "cripto";
  if (/acao|acoes|[A-Z]{4}[0-9]{1,2}|etf|bdr/.test(s)) return "renda_variavel";
  if (/cdb|lci|lca|cri|cra|debenture|tesouro|ntn|ltn|renda\s+fixa|pre|pos|ipca|selic/.test(s))
    return "renda_fixa";

  return "outros";
};

const parseDate = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const s = String(raw).trim();
  // ISO format already
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // DD/MM/YYYY
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  // MM/DD/YYYY (Avenue US format)
  const m2 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m2) return `${m2[3]}-${m2[1].padStart(2, "0")}-${m2[2].padStart(2, "0")}`;
  return null;
};

// ─── Interwin / Inter ────────────────────────────────────────────

export function normalizeInterwin(
  parsed: InterwinParsed,
  clientId: string,
  snapshotId: string | null | undefined
): NormalizedTx[] {
  const txs: NormalizedTx[] = [];
  const period = parsed.period || null;
  const refDate = parsed.ref_date || null;

  if (parsed.movimentacoes?.length) {
    for (const mov of parsed.movimentacoes) {
      txs.push({
        client_id: clientId,
        broker: "interwin",
        type: normalizeType(mov.tipo),
        ticker: null,
        asset_name: mov.tipo || "Movimentação Interwin",
        asset_class: "outros",
        currency: "BRL",
        quantity: null,
        unit_price: null,
        gross_value: mov.valor || 0,
        net_value: null,
        tx_date: parseDate(mov.data) || refDate || new Date().toISOString().slice(0, 10),
        ref_period: period,
        snapshot_id: snapshotId || null,
        source: "interwin_pdf",
        raw_data: mov,
      });
    }
  }

  if (parsed.assets_inter?.length) {
    for (const a of parsed.assets_inter) {
      if (a.aplicacoes && a.aplicacoes > 0) {
        txs.push({
          client_id: clientId,
          broker: "interwin",
          type: "aplicacao",
          ticker: a.ticker || null,
          asset_name: a.nome ?? a.ticker ?? "",
          asset_class: inferAssetClass(a.nome, a.estrategia, a.ticker, "interwin"),
          currency: "BRL",
          quantity: null,
          unit_price: null,
          gross_value: a.aplicacoes,
          net_value: null,
          tx_date: refDate ?? new Date().toISOString().slice(0, 10) ?? "",
          ref_period: period,
          snapshot_id: snapshotId || null,
          source: "interwin_pdf",
          raw_data: a,
        });
      }
      if (a.resgates && a.resgates > 0) {
        txs.push({
          client_id: clientId,
          broker: "interwin",
          type: "resgate",
          ticker: a.ticker || null,
          asset_name: a.nome ?? a.ticker ?? "",
          asset_class: inferAssetClass(a.nome, a.estrategia, a.ticker, "interwin"),
          currency: "BRL",
          quantity: null,
          unit_price: null,
          gross_value: -a.resgates,
          net_value: null,
          tx_date: refDate ?? new Date().toISOString().slice(0, 10) ?? "",
          ref_period: period,
          snapshot_id: snapshotId || null,
          source: "interwin_pdf",
          raw_data: a,
        });
      }
      if (a.eventos && a.eventos > 0) {
        txs.push({
          client_id: clientId,
          broker: "interwin",
          type: "evento",
          ticker: a.ticker || null,
          asset_name: a.nome ?? a.ticker ?? "",
          asset_class: inferAssetClass(a.nome, a.estrategia, a.ticker, "interwin"),
          currency: "BRL",
          quantity: null,
          unit_price: null,
          gross_value: a.eventos,
          net_value: null,
          tx_date: refDate ?? new Date().toISOString().slice(0, 10) ?? "",
          ref_period: period,
          snapshot_id: snapshotId || null,
          source: "interwin_pdf",
          raw_data: a,
        });
      }
    }
  }

  return txs;
}

// ─── XP Investimentos ────────────────────────────────────────────

export function normalizeXP(
  parsed: XPParsed,
  clientId: string,
  snapshotId: string | null | undefined,
  refDate?: string
): NormalizedTx[] {
  const txs: NormalizedTx[] = [];
  const period = parsed.period || null;
  const date = refDate || new Date().toISOString().slice(0, 10);

  const processAssets = (assets: XPAsset[] | undefined, subClass: string): void => {
    if (!assets?.length) return;
    for (const a of assets) {
      txs.push({
        client_id: clientId,
        broker: "xp",
        type: "aplicacao",
        ticker: a.n?.match(/[A-Z]{3,6}[0-9]{1,2}/)?.[0] || null,
        asset_name: a.n || "Ativo XP",
        asset_class: subClass,
        currency: "BRL",
        quantity: null,
        unit_price: null,
        gross_value: a.v || 0,
        net_value: a.liq || a.v || 0,
        tx_date: date,
        ref_period: period,
        snapshot_id: snapshotId || null,
        source: "xp_xlsx",
        raw_data: a,
      });
    }
  };

  if (parsed.assets_xp) {
    processAssets(parsed.assets_xp.posfix, "renda_fixa");
    processAssets(parsed.assets_xp.inflacao, "renda_fixa");
    processAssets(parsed.assets_xp.prefixado, "renda_fixa");
    processAssets(parsed.assets_xp.prev, "previdencia");
  }

  return txs;
}

// ─── BTG Pactual ─────────────────────────────────────────────────

export function normalizeBTG(
  parsed: BTGParsed,
  clientId: string,
  snapshotId: string | null | undefined,
  refDate?: string
): NormalizedTx[] {
  const txs: NormalizedTx[] = [];
  const date = refDate || new Date().toISOString().slice(0, 10);

  if (parsed.assets_btg?.length) {
    for (const a of parsed.assets_btg) {
      txs.push({
        client_id: clientId,
        broker: "btg",
        type: "aplicacao",
        ticker: a.code || null,
        asset_name: a.code || "Ativo BTG",
        asset_class: "renda_fixa",
        currency: "BRL",
        quantity: null,
        unit_price: null,
        gross_value: a.bruto || 0,
        net_value: a.liq || a.bruto || 0,
        tx_date: date,
        ref_period: null,
        snapshot_id: snapshotId || null,
        source: "btg_xlsx",
        raw_data: a,
      });
    }
  }

  return txs;
}

// ─── Avenue Securities ───────────────────────────────────────────

export function normalizeAvenue(
  parsed: AvenueParsed,
  clientId: string,
  snapshotId: string | null | undefined
): NormalizedTx[] {
  const txs: NormalizedTx[] = [];
  const period = parsed.period || null;
  const date = new Date().toISOString().slice(0, 10);

  if (parsed.positions?.length) {
    for (const p of parsed.positions) {
      txs.push({
        client_id: clientId,
        broker: "avenue",
        type: "aplicacao",
        ticker: p.ticker || null,
        asset_name: p.name || p.ticker || "Ativo Avenue",
        asset_class: "internacional",
        currency: "USD",
        quantity: p.shares || null,
        unit_price: p.price || null,
        gross_value: p.value || 0,
        net_value: null,
        tx_date: date,
        ref_period: period,
        snapshot_id: snapshotId || null,
        source: parsed.source || "avenue_pdf",
        raw_data: p,
      });
    }
  }

  return txs;
}

// ═══════════════════════════════════════════════════════
// ENTRY POINT — Normaliza qualquer resultado de parser
// ═══════════════════════════════════════════════════════

export function normalizeParserOutput(
  broker: string,
  parsedData: InterwinParsed | XPParsed | BTGParsed | AvenueParsed | unknown,
  clientId: string,
  snapshotId?: string,
  refDate?: string
): NormalizedTx[] {
  if (!parsedData || !clientId) return [];

  try {
    switch (broker) {
      case "interwin":
      case "inter":
        return normalizeInterwin(parsedData as InterwinParsed, clientId, snapshotId);
      case "xp":
        return normalizeXP(parsedData as XPParsed, clientId, snapshotId, refDate);
      case "btg":
        return normalizeBTG(parsedData as BTGParsed, clientId, snapshotId, refDate);
      case "avenue":
        return normalizeAvenue(parsedData as AvenueParsed, clientId, snapshotId);
      default:
        logWarn("transactions", `Broker "${broker}" sem normalizer — ignorando.`);
        return [];
    }
  } catch (e: unknown) {
    logError("transactions", e, { context: "normalizeParserOutput", broker, clientId });
    return [];
  }
}

export async function ingestTransactions(
  broker: string,
  parsedData: InterwinParsed | XPParsed | BTGParsed | AvenueParsed | unknown,
  clientId: string,
  snapshotId?: string,
  refDate?: string
): Promise<IngestResult> {
  const txs = normalizeParserOutput(broker, parsedData, clientId, snapshotId, refDate);
  if (!txs.length) return { count: 0, transactions: [] };

  const period = txs[0]?.ref_period;
  if (period) {
    try {
      await deleteTransactionsByPeriod(clientId, broker, period);
    } catch (e) {
      logWarn("transactions", `Falha ao limpar txs anteriores: ${(e as Error).message}`);
    }
  } else if (snapshotId) {
    try {
      await deleteTransactionsBySnapshot(clientId, snapshotId);
    } catch (e) {
      logWarn("transactions", `Falha ao limpar txs do snapshot: ${(e as Error).message}`);
    }
  }

  await insertTransactions(txs);
  return { count: txs.length, transactions: txs };
}
